import { ApiError, authApi } from '@/lib/api';
import type { LoginResult } from '@/lib/api';

/**
 * Every session in this browser shares one cookie jar, so a session cleared
 * in one tab is never valid in another. Broadcasting only speeds up a tab
 * that has not yet made a request discovering that; without
 * `BroadcastChannel` a tab just discovers it on its own next request.
 */
const SESSION_CHANNEL_NAME = 'ua_session';

class AuthStore {
  private session: LoginResult | null = null;
  /** `Date.now()` on this device when the current session answer arrived. */
  private receivedAt = 0;
  private inFlight: Promise<boolean> | null = null;
  private readonly listeners = new Set<() => void>();
  private readonly channel =
    typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(SESSION_CHANNEL_NAME);

  constructor() {
    this.channel?.addEventListener('message', () => this.clearLocal());
  }

  getSession(): LoginResult | null {
    return this.session;
  }

  getReceivedAt(): number {
    return this.receivedAt;
  }

  isAuthenticated(): boolean {
    return this.session !== null;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  setSession(session: LoginResult): void {
    this.session = session;
    this.receivedAt = Date.now();
    this.emit();
  }

  setSelectedBranch(branchId: string): void {
    if (!this.session) return;
    this.session = { ...this.session, selectedBranchId: branchId };
    this.emit();
  }

  clear(): void {
    this.clearLocal();
    // A `BroadcastChannel` never delivers a tab's own message back to itself.
    this.channel?.postMessage('cleared');
  }

  private clearLocal(): void {
    this.session = null;
    this.emit();
  }

  /**
   * Resolves the session once per page load. Route guards call this on every
   * navigation; refetching (and re-emitting) each time made a mounted layout
   * react to its own guard and navigate again in an endless loop.
   */
  async restore(): Promise<boolean> {
    if (this.session) return true;
    this.inFlight ??= this.load().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  /**
   * unified-access.md section 3.1: asks the server whether the session is
   * still valid once the time left has passed. A 401 clears the session (the
   * layout then returns to the login screen); a valid answer replaces it and
   * re-arms the expiry timer. Any other failure leaves it to the next request.
   */
  async revalidate(): Promise<void> {
    if (!this.session) return;
    await this.load().catch(() => undefined);
  }

  private async load(): Promise<boolean> {
    try {
      const session = await authApi.me();
      this.setSession(session);
      return true;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        this.clear();
        return false;
      }
      throw error;
    }
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}

export const authStore = new AuthStore();
